import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Divider, Grid, Header, Input, Tab } from 'semantic-ui-react';
import i18n from '~/components/i18n';
import Layout from '~/components/Layout'
import { RouteTypes } from '~/routes/constants';
import { StyledGridColumns, StyledGridRow, StyledSegment } from '~/pages/Dairy/MilkFixedPrice/ShowMilkFixedPrice/style';
import cloneDeep from 'lodash.clonedeep'
import TableComponent from '~/components/TableComponent';
import { useAuth } from '~/routes/components/AuthProvider';
import { formatNumberWithoutFractions, handleErrors } from '~/utils/helpers';
import { useDebounce } from 'use-lodash-debounce'
import { ADMIN_ROLE_SLUG, LIGHT_USER_ROLE_SLUG, OPERATIVE_ROLE_SLUG } from '~/Constants';
import styled from 'styled-components';
import { FatAndProteinQuery } from '~/QueryHooks/FatAndProteinQuery';
import FatAndProteinService from '~/api/services/fatAndProtein';

interface ResultDataType {
  key: string,
  value: number
}

const MilkOffersWrapper = styled.div`
 .title {
  font-size: 16px !important;
  font-weight: bold !important;
  margin-bottom: 1em;
  margin-top: 2em;
 }
`

const ResultsFatAndProteinPrice: React.FC = () => {
  const [isBusy, setIsBusy] = useState(false)
  const [showData, setShowData] = useState(null)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [idFarmerGroup, setIdFarmerGroup] = useState(null)
  const [dataFiltered, setDataFiltered] = useState({
    search: '',
    page: 0,
    rows: 10
  })
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const debouncedValue = useDebounce(search, 300)
  const { id } = useParams()
  const { systemData: { logged: { roleSlug }, amountUnit, amountVolume, amountPrice } } = useAuth()
  const priceLabel = `(${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit})`
  const displayOfferTable = roleSlug === ADMIN_ROLE_SLUG || roleSlug === OPERATIVE_ROLE_SLUG || roleSlug === LIGHT_USER_ROLE_SLUG
  const offersDataColumns = [
    {
      name: i18n.t('Supplier No.'),
      key: 'supplierNo'
    },
    {
      name: i18n.t('Company name'),
      key: 'companyName'
    },
    {
      name: t('Offer ID'),
      key: 'uniqueId'
    },
    {
      name: t('Offer date'),
      key: 'createdAt',
      type: 'date',
      format: 'DD.MM.YYYY HH:mm'
    },
    {
      name: t('Trading month'),
      key: 'date',
      type: 'date',
      format: 'MMM YYYY'
    },
    {
      name: i18n.t('Fat price (€/100kg)'),
      key: 'fatPrice',
      type: 'no-unit-number'
    },
    {
      name: i18n.t('Protein price (€/100kg)'),
      key: 'proteinPrice',
      type: 'no-unit-number'
    },
    {
      name: i18n.t('Price (€/100kg)'),
      key: 'price',
      type: 'no-unit-number'
    },
    {
      name: t('Offered amount (in {{amountUnit}})', { amountUnit }),
      key: 'amount',
      type: 'number'
    },
    {
      name: t('Confirmed amount (in {{amountUnit}})', { amountUnit }),
      key: 'confirmedAmount',
      type: 'number'
    },
    {
      name: t('Status'),
      key: 'statusLabel'
    }
  ]

  const [totalResultData, setTotalResultData] = useState({
    totalOffers: {
      key: 'offersCount',
      value: 0
    },
    totalOfferedAmount: {
      key: 'offeredAmount',
      value: 0
    },
    totalConfirmedAmount: {
      key: 'confirmedAmount',
      value: 0
    },
    total: {
      key: 'total',
      value: 0
    }
  })

  const resultsTableHeaders = [
    {
      name: i18n.t('Procurent month'),
      key: 'date',
      type: 'dateRibbon',
      format: 'MMM YYYY'
    },
    {
      name: i18n.t('Fat price (€/100kg)'),
      key: 'fatPrice',
      type: 'no-unit-number'
    },
    {
      name: i18n.t('Protein price (€/100kg)'),
      key: 'proteinPrice',
      type: 'no-unit-number'
    },
    {
      name: i18n.t('Number of offers'),
      key: 'offersCount'
    },
    {
      name: i18n.t('Offered amount (in {{amountUnit}})', { amountUnit }),
      key: 'offeredAmount',
      type: 'number'
    },
    {
      name: i18n.t('Fixed price amount (in {{amountUnit}})', { amountUnit }),
      key: 'confirmedAmount',
      type: 'number'
    }
  ]

  const {
    isLoading: isBusyOffersData,
    data: tradingOffers,
    refetch: fetchDataOffers,
    isFetching: isFetchingData
  } = FatAndProteinQuery.getMilkFixexPriceOffersData(
    id,
    idFarmerGroup?.farmerGroupId,
    { ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1 }
  )

  useEffect(() => {
    fetchShowData()
  }, [])

  useEffect(() => {
    if (dataFiltered && activeTabIndex > 0) {
      fetchDataOffers()
    }
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  async function fetchShowData() {
    setIsBusy(true)
    try {
      const showResponse = await FatAndProteinService.showTradingSlot(id)
      setShowData(showResponse.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  const {
    isLoading: isBusyData,
    data: milkOffers,
    refetch: fetchData
  } = FatAndProteinQuery.getMilkFixexPriceOffers(id, idFarmerGroup)

  useEffect(() => {
    if (milkOffers) {
      const tempTotalResultsData = cloneDeep(totalResultData)
      Object.values(tempTotalResultsData).forEach((resultData: ResultDataType) => {
        resultData.value = milkOffers.reduce(
          (previousValue, currentValue) => previousValue + parseInt(currentValue[resultData.key]),
          0
        )
      })
      setTotalResultData(tempTotalResultsData)
    }
  }, [milkOffers])

  useEffect(() => {
    if (activeTabIndex === 0) {
      setIdFarmerGroup(null)
    } else {
      setIdFarmerGroup({
        farmerGroupId: showData?.farmerGroups[activeTabIndex - 1]?.id
      })
    }
  }, [activeTabIndex])

  useEffect(() => {
    if (milkOffers) {
      fetchData()
    }
    if (displayOfferTable && activeTabIndex > 0) {
      setSearch('')
      setDataFiltered({
        search: '',
        page: 0,
        rows: 10
      })
    }
  }, [idFarmerGroup])

  function renderTable(rows) {
    return <>
      <div style={{ marginBottom: 20 }}>
        <TableComponent
          tableColumnHeaders={resultsTableHeaders}
          rows={rows}
          idKey={'date'}
          total={rows.length}
          removePagination
        />
      </div>
      {displayOfferTable && activeTabIndex > 0 && <MilkOffersWrapper>
        <p className='title'>{t('Trading Activities')}</p>
        <Input
          style={{ marginBottom: 10 }}
          icon={{ name: 'search', circular: true, link: true }}
          placeholder={t('Search...')}
          value={search}
          onChange={(e, data) => setSearch(data.value)}
        />
        <TableComponent
          tableColumnHeaders={offersDataColumns}
          dataFiltered={dataFiltered}
          handleChangeDataFiltered={(data) => setDataFiltered(data)}
          isFetchingData={isFetchingData}
          animated
          rows={tradingOffers?.rows || []}
          total={tradingOffers?.filtered || 0}
        />
      </MilkOffersWrapper>
      }
    </>
  }

  function renderInfoResults() {
    return <StyledSegment placeholder>
      <Grid columns={2} stackable>
        <Divider vertical />
        <Grid.Row verticalAlign='middle'>
          <Grid.Column>
            <Grid columns={4} stackable>
              <Grid.Row style={{ marginLeft: 10 }}>
                <Header>{showData.uniqueId}</Header>
              </Grid.Row>
              <StyledGridRow>
                <Grid.Column>{t('Created')}</Grid.Column>
                <StyledGridColumns>{dayjs(showData.createdAt).format('dddd, D MMMM YYYY HH:mm')}</StyledGridColumns>
                <StyledGridColumns></StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Start')}</Grid.Column>
                <StyledGridColumns>{dayjs(showData.dateFrom).format('dddd, D MMMM YYYY HH:mm')}</StyledGridColumns>
                <StyledGridColumns></StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('End')}</Grid.Column>
                <StyledGridColumns>{dayjs(showData.dateTo).format('dddd, D MMMM YYYY HH:mm')}</StyledGridColumns>
                <StyledGridColumns></StyledGridColumns>
              </StyledGridRow>
            </Grid>
          </Grid.Column>
          <Grid.Column>
            <Grid columns={2} stackable>
              <StyledGridRow>
                <Grid.Column>{t('Total offers')}</Grid.Column>
                <StyledGridColumns>{formatNumberWithoutFractions(totalResultData.totalOffers.value)}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Total offered amount (in {{amountUnit}})', { amountUnit })}</Grid.Column>
                <StyledGridColumns>{`${formatNumberWithoutFractions(totalResultData.totalOfferedAmount.value)} ${amountUnit}`}</StyledGridColumns>
              </StyledGridRow>
              <StyledGridRow>
                <Grid.Column>{t('Total confirmed amount (in {{amountUnit}})', { amountUnit })}</Grid.Column>
                <StyledGridColumns>{`${formatNumberWithoutFractions(totalResultData.totalConfirmedAmount.value)} ${amountUnit}`}</StyledGridColumns>
              </StyledGridRow>
            </Grid>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </StyledSegment>
  }

  function renderShowData() {
    const farmerGroupTabs = showData.farmerGroups.map(farmerGroup => {
      return {
        menuItem: farmerGroup.name,
        render: () => <>
          <div style={{ marginBottom: 20 }}>
            {showData && renderInfoResults()}
          </div>
          {renderTable(milkOffers)}
        </>
      }
    })
    const panes = [
      {
        menuItem: t('Fat & protein trading slot offers'),
        render: () => <>
          <div style={{ marginBottom: 20 }}>
            {showData && renderInfoResults()}
          </div>
          {renderTable(milkOffers)}
        </>
      },
      ...farmerGroupTabs
    ]

    return <Tab
      menu={{ secondary: true, pointing: true }}
      //@ts-ignore
      onTabChange={(e, data) => setActiveTabIndex(parseInt(data.activeIndex))}
      panes={panes}
    />
  }

  return <Layout
    segmented
    title={t('Fat & protein trading slot results')}
    close={`${RouteTypes.FAT_PROTEIN_PRICE}#${showData?.status}`}
    isBusy={isBusyData || isBusy}
  >
    {milkOffers && showData && renderShowData()}
  </Layout>
}
export default ResultsFatAndProteinPrice
